import { PaymentPlan } from '@models/configuration/model';
import { Dayjs } from 'dayjs';
import { Dates } from './dates';

export const getEffectiveScheduleEndDate = (plan: PaymentPlan): Dayjs | null => {
  if (plan.planEndDate) {
    return Dates.fromString(plan.planEndDate, 'YYYY-MM-DD');
  }

  if (plan.scheduleEndDate) {
    // can't calculate end date if we don't have a start date (sanity check, should never happen, a plan should always have a start date)
    if (!plan.planStartDate) {
      return null;
    }

    const startDate = Dates.fromString(plan.planStartDate);

    let effectiveScheduleEndDate = Dates.fromString(`${plan.scheduleEndDate}/${Dates.todaysDate().year()}`, 'MM/DD/YYYY');
    if (Dates.isBefore(effectiveScheduleEndDate, startDate)) {
      effectiveScheduleEndDate = effectiveScheduleEndDate.add(1, 'y');
    }

    return effectiveScheduleEndDate;
  }

  // if plan doesn't have planEndDate or scheduleEndDate, we can't determine renewal period
  return null;
};

export const isEligibleForRenewal = (plan: PaymentPlan): boolean => {
  // We check that a plan hasn't been renewed before determining if a plan is within renewal lead time
  // After investigation, it seems the only time this flag is set is when a payment plan has been manually renewed in Admin UI
  // Business claims they do not use the manual renewal feature for payment plans. However, since it is functionality that can be used, we've kept the check here
  if (plan.hasBeenRenewed) {
    return false;
  }

  const effectiveScheduleEndDate = getEffectiveScheduleEndDate(plan);

  if (!effectiveScheduleEndDate) {
    return false;
  }

  const today = Dates.todaysDate();

  // if today is past the schedule end date, plan is not eligible for renewal and should be filtered out
  if (Dates.isAfter(today, effectiveScheduleEndDate)) {
    return false;
  }

  const renewalDate = effectiveScheduleEndDate.subtract(plan.renewalLeadTimeDays, 'days');

  // allow plan to be renewed if today is after the renewal date calculated (based on renewalLeadTimeDays)
  // and plan hasn't already been filtered out based on end dates (it is within the "renewal lead time")
  return Dates.isSameOrAfter(today, renewalDate);
};

export const getRenewalStartDate = (plan: PaymentPlan): string | null => {
  const effectiveScheduleEndDate = getEffectiveScheduleEndDate(plan);

  if (!effectiveScheduleEndDate) {
    return null;
  }

  return effectiveScheduleEndDate.subtract(plan.renewalLeadTimeDays, 'days').toString();
};
